import React, { useState, useContext } from "react";
import { Form, Button, Row, Table, InputGroup, Modal, Card } from "react-bootstrap";
import { ToastContainer } from 'react-toastify';
import { Receipt } from "react-bootstrap-icons";
import axiosInstance from "../../../utils/axios";
import { formatDisplayDate, formatDisplayTime } from '../../../utils/formatter';
import AuthContext from '../../../context/AuthContext';
import { showToast } from '../../reusables/CommonFunction';
import PrescriptionModal from './PrescriptionModal';
import CustomPagination from '../../reusables/CustomPagination';
import BusyLoaderOverlay from '../../reusables/BusyLoaderOverlay';

const PatientPrescription = () => {
    let { userDetails } = useContext(AuthContext)
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState("")
    const [isModalOpen, setModalOpen] = useState(false);
    const [isPrescriptionModalOpen, setPrescriptionModalOpen] = useState(false);
    const [patientDetails, setPatientDetails] = useState([])
    const [patientPrescriptionDetails, setPatientPrescriptionDetails] = useState([]);
    const [prescriptionDetails, setPrescriptionDetails] = useState({});
    const [selectedPatientId, setSelectedPatientId] = useState(null);
    const [selectedPatientEmail, setSelectedPatientEmail] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const pageSize = parseInt(process.env.REACT_APP_PAGINATION_PAGE_SIZE);
    const salutation = { '1': 'DR.', '2': 'MR.', '3': 'MRS.', '4': 'MISS', '5': 'MASTER' };
    const gender = { 'M': 'Male', 'F': "Female", 'O': "Others" };


    const onPressSearchPatient = (oEvent) => {
        oEvent.preventDefault();
        if (!searchQuery.trim()) {
            showToast("error", "Please enter a patient name or case number to search.");
            return;
        }

        setLoading(true);
        axiosInstance
            .get(`/patients/`, {
                params: {
                    q: searchQuery,
                    clinicId: userDetails.clinic_id
                }
            })
            .then(response => {
                if (response.status === 200) {
                    setPatientDetails(response.data.data)
                }
            })
            .catch(error => {
                showToast("error", "Failed to fetch patient list");
            })
            .finally(() => {
                setLoading(false);
            });
    }

    const onPressConfirmPatient = (oEvent, patient) => {
        setSearchQuery(`${salutation[patient.salutation]} ${patient.patient_name}`);
        setSelectedPatientId(patient.patient_id);
        setModalOpen(false);
        setPatientPrescriptionDetails([]);
        setCurrentPage(1);
        setTotalCount(0);

        onPressSearchPatientPrescription(oEvent, patient.patient_id);
    }

    const onPressSearchPatientPrescription = (oEvent, pPatientId) => {
        oEvent.preventDefault();
        const patientId = pPatientId || "";

        if (!patientId) {
            showToast("error", "Please select a patient to view prescriptions.");
            return;
        }

        setLoading(true);
        axiosInstance
            .get(`patients/patient_prescriptions`, {
                params: {
                    patientId: patientId,
                    clinicId: userDetails.clinic_id,
                    page: currentPage,
                }
            })
            .then(response => {
                if (response.status === 200) {
                    setPatientPrescriptionDetails(response.data.data);
                    setTotalCount(response.data.count);
                }
            })
            .catch(error => {
                showToast("error", "Failed to fetch patient details");
            })
            .finally(() => {
                setLoading(false);
            });
    }

    const onPressViewPrescription = (oEvent, pPatient) => {
        oEvent.preventDefault();
        setPrescriptionModalOpen(true);
        setSelectedPatientEmail(pPatient.email_id);

        setPrescriptionDetails({
            patient_type: pPatient.patient_type,
            patient_name: `${salutation[pPatient.salutation]} ${pPatient.patient_name}`,
            patient_case_number: pPatient.patient_case_number,
            age: pPatient.age,
            gender: gender[pPatient.gender],
            diagnosis_1: pPatient.diagnosis_1 || "",
            diagnosis_2: pPatient.diagnosis_2 || "",
            diagnosis_3: pPatient.diagnosis_3 || "",
            diagnosis_4: pPatient.diagnosis_4 || "",
            investigation: pPatient.investigation || "",
            remedy: pPatient.remedy || "",
            advice: pPatient.advice || "",
            followUp: pPatient.follow_up || "",
            next_appointment_date: pPatient.next_appointment_date,
            next_appointment_time: pPatient.next_appointment_time,
            visit_date: pPatient.visit_date,
            user_type: userDetails.user_type,
            clinic_name: userDetails.clinic_name,
            doctor_name: userDetails.doctor_name,
            doctor_profession: userDetails.doctor_profession,
            registration_number: userDetails.registration_number,
            doctor_degree: userDetails.doctor_degree,
            doctor_mobile_number: userDetails.mobile_number,
            doctor_alternate_mobile_number: userDetails.alternate_mobile_number,
            doctor_email_id: userDetails.email_id,
            clinic_website: userDetails.clinic_website,
            clinic_address: userDetails.clinic_address,
        });
    }

    return (
        <>
            <ToastContainer />
            <Row className="ml-3 mr-3 mb-3">
                <div className='col-md-12'>
                    <Card className="mb-4">
                        <Card.Body>
                            <div className="d-flex align-items-center gap-2 mb-3">
                                <Receipt size={20} className="text-muted" />
                                <h5 className="mb-0">Patient Prescriptions</h5>
                            </div>

                            <InputGroup className="mb-3 col-md-4 pl-0" >
                                <Form.Control
                                    readOnly
                                    type="text"
                                    name="searchQuery"
                                    placeholder="Click here to select a patient"
                                    value={searchQuery}
                                    onClick={() => setModalOpen(true)}
                                />
                                <Button type="submit" variant="outline-secondary" onClick={(oEvent) => {
                                    if (!selectedPatientId) {
                                        showToast("error", "Please select a patient to view prescriptions.");
                                        return;
                                    }
                                    onPressSearchPatientPrescription(oEvent, selectedPatientId)
                                }}>
                                    Go
                                </Button>
                            </InputGroup>
                        </Card.Body>
                    </Card>

                    <Modal size="lg" backdrop="static" keyboard={false} centered show={isModalOpen} onHide={() => { setModalOpen(false) }} >
                        <Modal.Header closeButton>
                            <Modal.Title>Search Patient</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <Row className="ml-3 mr-3 mb-3">
                                <div className='col-md-12'>
                                    <InputGroup className="mb-3 pl-0" >
                                        <Form.Control
                                            type="text"
                                            name="searchQuery"
                                            autoFocus
                                            onChange={(oEvent) => setSearchQuery(oEvent.target.value)}
                                            onKeyDown={(oEvent) => oEvent.key === "Enter" && onPressSearchPatient(oEvent)}
                                            placeholder="Type patient name or case number and press Enter..."
                                        />
                                        <Button type="submit" variant="outline-secondary" onClick={onPressSearchPatient}>
                                            Search
                                        </Button>
                                    </InputGroup>

                                    {/* If the count is 0 then hide the table */}
                                    {patientDetails.length > 0 ?
                                        <Table bordered hover responsive>
                                            <thead light="true">
                                                <tr>
                                                    <th style={{ width: "2rem" }}>#</th>
                                                    <th style={{ width: "18rem" }}>Patient Name</th>
                                                    <th style={{ width: "10rem" }}>Case Number</th>
                                                    <th style={{ width: "10rem" }}>Mobile Number</th>
                                                    <th style={{ width: "7rem" }}></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {patientDetails.map((patient, index) => {
                                                    return [
                                                        <tr key={patient.patient_id}>
                                                            <td>{index + 1}</td>
                                                            <td className='text-left'>{`${salutation[patient.salutation]} ${patient.patient_name}`}</td>
                                                            <td className='text-left'>{patient.patient_case_number}</td>
                                                            <td className='text-left'>{patient.mobile_number}</td>
                                                            <td><Button type='button' variant='info' size='sm' onClick={oEvent => onPressConfirmPatient(oEvent, patient)} >Confirm</Button></td>
                                                        </tr>
                                                    ]
                                                })}
                                            </tbody>
                                        </Table>
                                        : <> </>}
                                </div>
                            </Row>

                        </Modal.Body>
                    </Modal>

                    {/* If the count is 0 then hide the table */}
                    {patientPrescriptionDetails.length > 0 ?
                        <>
                            <h6 className="mt-4 mb-2">
                                Prescriptions for <strong>{searchQuery}</strong>
                            </h6>
                            <Table bordered hover responsive>
                                <thead light="true">
                                    <tr>
                                        <th style={{ width: "2rem" }}>#</th>
                                        <th>Visit Date</th>
                                        <th>Visit Type</th>
                                        <th>Next Appointment Date</th>
                                        <th>Next Appointment Time</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {patientPrescriptionDetails.map((patientPrescription, index) => {
                                        return [
                                            <tr key={patientPrescription.patient_id}>
                                                <td>{index + 1}</td>
                                                <td>{formatDisplayDate(patientPrescription.visit_date)}</td>
                                                <td>{patientPrescription.source == 'N' ? '1st Consulatation' : 'Follow-up'}</td>
                                                <td>{patientPrescription.next_appointment_date ? formatDisplayDate(patientPrescription.next_appointment_date) : 'N/A'}</td>
                                                <td>{patientPrescription.next_appointment_time ? formatDisplayTime(patientPrescription.next_appointment_time) : 'N/A'}</td>
                                                <td>
                                                    <Button variant='outline-primary' onClick={oEvent => onPressViewPrescription(oEvent, patientPrescription)}>View Prescription</Button>
                                                </td>
                                            </tr>
                                        ]
                                    })}
                                </tbody>
                            </Table>

                            <CustomPagination
                                totalCount={totalCount}
                                currentPage={currentPage}
                                pageSize={pageSize}
                                onPageChange={setCurrentPage} />
                        </>
                        : <> </>}

                    {isPrescriptionModalOpen && (
                        <PrescriptionModal
                            isOpen={isPrescriptionModalOpen}
                            onClose={() => setPrescriptionModalOpen(false)}
                            prescriptionDetails={prescriptionDetails}
                            patientEmailAddress={selectedPatientEmail} />
                    )}

                </div>
            </Row>
            <BusyLoaderOverlay show={loading} />
        </>
    );
};

export default PatientPrescription;